import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, Typography } from '@material-ui/core';
import { MOBILE_QUERY } from 'components/Page/useIsMobile';

export interface ProfileStat {
  label: string;
  value: React.ReactNode;
  /** small text under the value */
  hint?: string;
}

interface Props {
  image?: string;
  /** user avatars are round, class icons aren't */
  roundImage?: boolean;
  /** ring around the image, e.g. the class color */
  accent?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** e.g. "Last game ..." */
  meta?: React.ReactNode;
  stats: ProfileStat[];
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, auto) minmax(0, 1fr)',
    alignItems: 'center',
    gap: `${theme.spacing(3)}px`,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    borderRadius: 10,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'rgba(20, 20, 20, 0.92)',
    overflow: 'hidden',
    [MOBILE_QUERY]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: `${theme.spacing(2)}px`,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
  },
  glow: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: 'none',
    opacity: 0.18,
  },
  identity: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  image: {
    width: 84,
    height: 84,
    flexShrink: 0,
    // flexbox gap isn't supported by Electron 8's Chromium, so the spacing is a margin
    marginRight: theme.spacing(2.5),
    objectFit: 'cover',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.04)',
    [MOBILE_QUERY]: {
      width: 60,
      height: 60,
      marginRight: theme.spacing(1.75),
    },
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.15,
    wordBreak: 'break-word',
    [MOBILE_QUERY]: {
      fontSize: '1.5rem',
    },
  },
  subtitle: {
    marginTop: 4,
    opacity: 0.85,
  },
  meta: {
    marginTop: 4,
    fontSize: '0.85em',
    opacity: 0.6,
  },
  stats: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: `${theme.spacing(1.5)}px`,
    [MOBILE_QUERY]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: `${theme.spacing(1)}px`,
    },
  },
  stat: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    [MOBILE_QUERY]: {
      padding: theme.spacing(0.75, 1.25),
    },
  },
  statLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.6,
  },
  statValue: {
    marginTop: 2,
    fontSize: '1.6rem',
    fontWeight: 800,
    lineHeight: 1.2,
    [MOBILE_QUERY]: {
      fontSize: '1.2rem',
    },
  },
  statHint: {
    fontSize: '0.75rem',
    opacity: 0.5,
  },
}));

/** The top of a character or player profile: who it is, and their headline numbers. */
export const ProfileHeader: React.FC<Props> = ({image, roundImage, accent, title, subtitle, meta, stats}) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      {accent && <div className={classes.glow} style={{background: `radial-gradient(circle at 0% 50%, ${accent} 0%, transparent 45%)`}} />}
      <div className={classes.identity}>
        {image &&
          <img className={classes.image} src={image}
            style={{borderRadius: roundImage ? '50%' : undefined, boxShadow: accent ? `0 0 0 3px ${accent}` : '0 0 0 2px rgba(255,255,255,0.15)'}} />
        }
        <div style={{minWidth: 0}}>
          <Typography variant="h4" className={classes.title}>{title}</Typography>
          {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
          {meta && <div className={classes.meta}>{meta}</div>}
        </div>
      </div>
      <div className={classes.stats}>
        {stats.map((stat) => (
          <div key={stat.label} className={classes.stat}>
            <div className={classes.statLabel}>{stat.label}</div>
            <div className={classes.statValue}>{stat.value}</div>
            {stat.hint && <div className={classes.statHint}>{stat.hint}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A rank like "#3", or a dash when there isn't one. */
export const rankValue = (rank?: number | null) => rank
  ? <span>#{rank}</span>
  : <Tooltip title="Not ranked yet"><span style={{opacity: 0.4}}>-</span></Tooltip>;

/** An MMR, or a dash when there isn't one. */
export const mmrValue = (mmr?: number | null) => mmr
  ? <span>{Math.round(mmr)}</span>
  : <Tooltip title="Not ranked yet"><span style={{opacity: 0.4}}>-</span></Tooltip>;
